import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';

import { usePermission } from '../../permissions';
import { getTravelFileSavedCards } from '../../api/customerCards/api';
import {
    SAVED_CARD_CHARGE_ASKS_CVV_AND_HOLDER_ID,
    type CardOwnerPassenger,
    type SavedCustomerCard
} from '../../api/customerCards/cardHelpers';
import { SPLIT_MIN_DAYS_BEFORE_TRIP } from '../../api/pelecardUtils/pelecardInstallments';
import SavedCardPickerView, { PAGE_OPTION } from './SavedCardPickerView';

/**
 * Where the choice lives in the receipt form's values. Deliberately one object
 * so the forms can strip it from the document in one `omit` — the CVV / holder
 * ID (when switched on) must never be written to `documentinfo`.
 *
 * Every field is written on its own (`setFieldValue('savedCardPayment.cardId')`),
 * from an event, never as a whole object rebuilt from render-time values — a
 * stale render would otherwise overwrite a newer choice.
 */
export type SavedCardPaymentState = {
    /** null = take the payment on Pelecard's page (a new card). */
    cardId: number | null;
    /** The chosen card's owner, recorded from the card itself when it is picked. */
    cardPassengerId: number | null;
    /** Plain instalments for a saved-card charge; always one of the offered counts. */
    numberOfPayments: number;
    /**
     * The paying passenger, chosen on the picker — Financial receipt only (scope
     * `travelFile`), whose travel-file receipt names no passenger. The travel-file
     * receipt reads its payer from the beneficiary at submit time instead.
     */
    payerPassengerId: number | string | null;
    /** Only when SAVED_CARD_CHARGE_ASKS_CVV_AND_HOLDER_ID is on. Never stored. */
    cvv2?: string;
    holderIdNumber?: string;
};

export const SAVED_CARD_FIELD = 'savedCardPayment';

export const getEmptySavedCardPayment = (): SavedCardPaymentState => ({
    cardId: null,
    cardPassengerId: null,
    numberOfPayments: 1,
    payerPassengerId: null
});

type Props = {
    travelFileId?: number | string | null;
    /**
     * `payer` (the travel-file receipt): the payer is `passengerId`, the form's
     * beneficiary — no passenger, no cards.
     * `travelFile` (the Financial receipt, which names no passenger): the agent
     * picks the paying passenger here, and only that passenger's cards are offered.
     */
    scope: 'payer' | 'travelFile';
    /** The paying passenger, for scope `payer`. */
    passengerId?: number | string | null;
    /** The receipt's ISO currency — a plain instalment split is offered for
     *  USD only (mirrors tourRegistrationApp's canSplitPlain); it settles in
     *  ILS at the wire level regardless (see chargeSavedCard.ts). ILS is
     *  reserved for a credit deal instead. */
    currencyCode?: string | null;
    disabled?: boolean;
    /** Why a passenger payer is not available (e.g. a "New" beneficiary with no passenger row). */
    unavailableReason?: string;
};

const field = (name: keyof SavedCardPaymentState) => `${SAVED_CARD_FIELD}.${name}`;

/**
 * Saved-card choice on a card receipt: "new card on the payment page" (saved for
 * a passenger payer automatically once the receipt exists — see
 * saveCardAfterPagePayment) or one of the paying passenger's saved cards,
 * with the number of payments the payment page would allow.
 *
 * Disabled-with-tooltip, never hidden, when the user lacks a permission or a card
 * cannot be charged, so the agent can see why.
 */
const SavedCardPicker: FC<Props> = ({ travelFileId, scope, passengerId, currencyCode, disabled, unavailableReason }) => {
    const payerOnly = scope === 'payer';
    const { values, setFieldValue } = useFormikContext<any>();
    const state: SavedCardPaymentState = { ...getEmptySavedCardPayment(), ...(values?.[SAVED_CARD_FIELD] || {}) };

    const canView = usePermission('customer_card:view');
    const canCharge = usePermission('customer_card:charge');

    const [cards, setCards] = useState<SavedCustomerCard[]>([]);
    const [passengers, setPassengers] = useState<CardOwnerPassenger[]>([]);
    // Still fetched with the rest of the scope (and still what the payment
    // page gates credit on); not read here any more, since a saved card is
    // always a single payment regardless of how far off the trip is.
    const [, setTripDate] = useState<string | null>(null);
    const [fullPaymentWindowDays, setFullPaymentWindowDays] = useState(SPLIT_MIN_DAYS_BEFORE_TRIP);
    const [bookedThroughAgent, setBookedThroughAgent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');

    const setSub = useCallback(
        (name: keyof SavedCardPaymentState, value: unknown) => setFieldValue(field(name), value, false),
        [setFieldValue]
    );

    useEffect(() => {
        if (!travelFileId || !canView) {
            setCards([]);
            setPassengers([]);
            setTripDate(null);
            setFullPaymentWindowDays(SPLIT_MIN_DAYS_BEFORE_TRIP);
            setBookedThroughAgent(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setLoadError('');
        getTravelFileSavedCards(travelFileId)
            .then((result) => {
                if (cancelled) return;
                setCards(result.cards);
                setPassengers(result.passengers);
                setTripDate(result.tripDate);
                setFullPaymentWindowDays(result.fullPaymentWindowDays);
                setBookedThroughAgent(result.bookedThroughAgent);
            })
            .catch((err) => {
                if (cancelled) return;
                setLoadError(err?.response?.data?.message || 'Could not load saved cards');
            })
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [travelFileId, canView]);

    const payerId = payerOnly ? passengerId ?? null : state.payerPassengerId;

    const visibleCards = useMemo(
        () => (payerId ? cards.filter((card) => String(card.passengerId) === String(payerId)) : []),
        [cards, payerId]
    );

    // A chosen card that is no longer offered (payer changed) falls back to the
    // payment page rather than silently charging someone else's card.
    useEffect(() => {
        if (state.cardId && !loading && !visibleCards.some((card) => card._id === state.cardId)) {
            setSub('cardId', null);
            setSub('cardPassengerId', null);
        }
    }, [visibleCards, loading, state.cardId, setSub]);

    // A plain instalment split is offered for a USD receipt ONLY — exactly
    // tourRegistrationApp's canSplitPlain, for a saved card same as a new one.
    // It settles in ILS at the wire level regardless (the server converts it;
    // see chargeSavedCard.ts). An ILS receipt gets no plain split at all —
    // that currency is reserved for a credit deal instead (see canCreditDeal
    // below), mirroring tourRegistrationApp's mutually-exclusive split/credit
    // currencies.
    const isUsd = String(currencyCode || '').toUpperCase() === 'USD';
    // Always a single payment. The only multi-payment product is a credit
    // deal (עסקת קרדיט, 3..18 — see pelecardInstallments.ts), and
    // `chargeSavedCard` refuses a credit deal on a saved card, so there is no
    // count above 1 this dialog could offer that the server would accept.
    // Kept as a list so the existing selector renders unchanged.
    const paymentOptions = useMemo(() => [1], []);
    useEffect(() => {
        if (!paymentOptions.includes(Number(state.numberOfPayments) || 1)) setSub('numberOfPayments', 1);
    }, [paymentOptions, state.numberOfPayments, setSub]);

    const hasPayer = !!payerId;
    const pickerDisabled = disabled || !canView || !hasPayer || bookedThroughAgent;

    const pickerTooltip = !canView
        ? 'You do not have permission to view saved cards'
        : bookedThroughAgent
          ? 'This travel file is paid by its travel agent — saved cards are for passenger payers only'
          : !hasPayer
            ? payerOnly
                ? unavailableReason || 'Saved cards are offered for a passenger payer only'
                : 'Choose the paying passenger to see their saved cards'
            : '';

    const cardDisabledReason = (card: SavedCustomerCard) =>
        card.isExpired
            ? 'This card has expired and cannot be charged'
            : !canCharge
              ? 'You do not have permission to charge saved cards'
              : '';

    const handleCardChange = (next: string) => {
        if (next === PAGE_OPTION) {
            setSub('cardId', null);
            setSub('cardPassengerId', null);
            return;
        }
        // The owner is taken from the card that was clicked, not from whatever
        // the form held when this rendered.
        const picked = visibleCards.find((card) => String(card._id) === next);
        if (!picked) return;
        setSub('cardId', picked._id);
        setSub('cardPassengerId', picked.passengerId);
        setSub('numberOfPayments', 1);
        setSub('cvv2', '');
        setSub('holderIdNumber', '');
    };

    return (
        <SavedCardPickerView
            disabled={disabled}
            showPayerSelect={!payerOnly}
            passengers={passengers}
            payerPassengerId={state.payerPassengerId}
            onPayerChange={(next) => {
                setSub('payerPassengerId', next || null);
                setSub('cardId', null);
                setSub('cardPassengerId', null);
            }}
            payerSelectDisabled={!!disabled || !canView || bookedThroughAgent || !passengers.length}
            payerSelectTooltip={
                !canView
                    ? 'You need permission to view saved cards to choose the paying passenger'
                    : bookedThroughAgent
                      ? 'This travel file is paid by its travel agent'
                      : ''
            }
            payerHelperText={
                canView && !bookedThroughAgent && !state.payerPassengerId
                    ? 'Needed to pay with a saved card or to save this card'
                    : ''
            }
            cards={visibleCards}
            selectedValue={state.cardId ? String(state.cardId) : PAGE_OPTION}
            onSelect={handleCardChange}
            listTooltip={pickerTooltip}
            cardsDisabled={!!pickerDisabled}
            cardDisabledReason={cardDisabledReason}
            loading={loading}
            loadError={loadError}
            showEmpty={
                !loading && canView && hasPayer && !bookedThroughAgent && !visibleCards.length && !loadError
            }
            cardChosen={!!state.cardId}
            paymentOptions={paymentOptions}
            numberOfPayments={Number(state.numberOfPayments) || 1}
            onNumberOfPaymentsChange={(count) => setSub('numberOfPayments', count)}
            paymentsTooltip={
                paymentOptions.length <= 1
                    ? !isUsd
                        ? 'Instalments are available in USD only'
                        : `Instalments are not available for this trip (less than ${fullPaymentWindowDays} days away, or no start date)`
                    : ''
            }
            showCreditUnavailable={!!state.cardId && String(currencyCode || '').toUpperCase() === 'ILS'}
            askCvvAndHolderId={SAVED_CARD_CHARGE_ASKS_CVV_AND_HOLDER_ID}
            cvv2={state.cvv2 || ''}
            onCvv2Change={(value) => setSub('cvv2', value)}
            holderIdNumber={state.holderIdNumber || ''}
            onHolderIdNumberChange={(value) => setSub('holderIdNumber', value)}
        />
    );
};

export default memo(SavedCardPicker);
